<template>
    <div
      class="switch-wrapper flex items-center cursor-pointer"
      :class="disabled ? 'cursor-not-allowed opacity-60' : ''"
      @mouseenter="isHover = true"
      @mouseleave="isHover = false"
    >
      <div
        class="switch-container"
        :class="[
          sizeClass,
          `color-${color}`,
          `appearance-${computedAppearance}`,
          { 'is-checked': isChecked, 'is-disabled': disabled, 'is-hover': isHover },
        ]"
        :style="containerStyle"
        @click="toggle"
      >
        <div class="switch-circle" :class="circleClass" :style="circleStyle">
          <!-- Circle fill for outlined appearance or dynamic OFF state -->
          <div v-if="shouldShowCircleFill" class="circle-fill" :style="circleFillStyle"></div>
        </div>
      </div>
      <span v-if="label || $slots.default" class="ml-2 text-sm switch-label">
        <slot>{{ label }}</slot>
      </span>
    </div>
  </template>
  
  <script>
  export default {
    name: 'NewLibrarySwitch',
    props: {
      // Vue 3 v-model support
      modelValue: {
        type: Boolean,
        default: undefined,
      },
      // Backward compatibility
      value: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      size: {
        type: String,
        default: 'large', // large, small
        validator: (val) => ['large', 'small'].includes(val),
      },
      color: {
        type: String,
        default: 'dark', // dark, blue, cyan
        validator: (val) => ['dark', 'blue', 'cyan'].includes(val),
      },
      filled: {
        type: Boolean,
        default: true,
      },
      toggleStyle: {
        type: String,
        default: 'static', // static, dynamic
        validator: (val) => ['static', 'dynamic'].includes(val),
      },
      label: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        isHover: false,
      };
    },
    computed: {
      isChecked() {
        // Prefer modelValue (Vue 3 v-model), fallback to value
        return this.modelValue !== undefined ? this.modelValue : this.value;
      },
      computedAppearance() {
        // For dynamic toggleStyle, appearance changes based on toggle state
        if (this.toggleStyle === 'dynamic') {
          return this.isChecked ? 'filled' : 'outlined';
        }
        // Otherwise use the filled prop
        return this.filled ? 'filled' : 'outlined';
      },
      sizeClass() {
        return this.size === 'large' ? 'size-large' : 'size-small';
      },
      circleClass() {
        return '';
      },
      shouldShowCircleFill() {
        // Show circle fill for outlined appearance
        return this.computedAppearance === 'outlined';
      },
      circleSize() {
        return this.size === 'large' ? 22 : 18;
      },
      colorMap() {
        return {
          dark: {
            filled: '#374957',
            filledHover: '#004879',
            outlined: '#374957',
            outlinedHover: '#1E4670',
            circleFill: '#374957',
            circleFillHover: '#004879',
          },
          blue: {
            filled: '#5587BC',
            filledHover: '#81B0E1',
            outlined: '#5587BC',
            outlinedHover: '#81B0E1',
            circleFill: '#81B0E1',
            circleFillHover: '#81B0E1',
          },
          cyan: {
            filled: '#8BBDC7',
            filledHover: '#50C5CE',
            outlined: '#8BBDC7',
            outlinedHover: '#8BBDC7',
            circleFill: '#50C5CE',
            circleFillHover: '#50C5CE',
          },
        };
      },
      containerStyle() {
        if (this.disabled) {
          // Disabled states
          if (this.computedAppearance === 'outlined') {
            return {
              border: '1px solid #E0E0E0',
              background: '#FFF',
            };
          }
          return {
            background: '#999',
          };
        }
  
        const colors = this.colorMap[this.color];
        const isToggled = this.isChecked;
        const isHover = this.isHover;
  
        if (this.computedAppearance === 'filled') {
          // Filled appearance - same background regardless of toggle state
          return {
            background: isHover ? colors.filledHover : colors.filled,
          };
        } else {
          // Outlined appearance
          const bgMap = {
            dark: isToggled ? (isHover ? '#E8F4FD' : '#F9FBFD') : isHover ? '#E8F4FD' : '#F9FBFD',
            blue: isToggled
              ? isHover
                ? 'linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%)'
                : '#F9FBFD'
              : isHover
              ? 'linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%)'
              : '#F9FBFD',
            cyan: isToggled
              ? isHover
                ? 'linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%)'
                : '#FDF9F6'
              : isHover
              ? 'linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%)'
              : '#FDF9F6',
          };
  
          return {
            border: `1px solid ${isHover ? colors.outlinedHover : colors.outlined}`,
            background: bgMap[this.color],
          };
        }
      },
      circleStyle() {
        const baseSize = this.circleSize;
        const transform = this.isChecked ? (this.size === 'large' ? 'translateX(22px)' : 'translateX(18px)') : 'translateX(0)';
  
        return {
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          transform,
          transition: 'all 0.3s ease',
        };
      },
      circleFillStyle() {
        // Only for outlined appearance - inner circle fill
        if (this.disabled) {
          return {
            background: '#E0E0E0',
          };
        }
  
        const colors = this.colorMap[this.color];
        return {
          background: this.isHover ? colors.circleFillHover : colors.circleFill,
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          transition: 'background 0.3s ease',
        };
      },
    },
    methods: {
      toggle() {
        if (this.disabled) return;
        const newValue = !this.isChecked;
        // Vue 3 v-model event
        this.$emit('update:modelValue', newValue);
        // Backward compatibility event
        this.$emit('input', newValue);
      },
    },
  };
  </script>
  
  <style scoped>
  .switch-wrapper {
    display: inline-flex;
    align-items: center;
  }
  
  .switch-container {
    display: flex;
    padding: 2px;
    align-items: center;
    border-radius: 13px;
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }
  
  .switch-container.size-large {
    width: 48px;
    height: 26px;
  }
  
  .switch-container.size-small {
    width: 40px;
    height: 22px;
  }
  
  .switch-circle {
    border-radius: 50%;
    background: linear-gradient(90deg, #e6edf2 0%, #eaf8f9 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
    box-sizing: border-box;
  }
  
  /* Outlined appearance - circle should be empty, only showing the fill */
  .appearance-outlined .switch-circle {
    background: transparent;
  }
  
  /* Disabled styles */
  .is-disabled .switch-circle {
    background: #e0e0e0 !important;
  }
  
  .circle-fill {
    transition: background 0.3s ease;
  }
  
  .switch-label {
    color: #333;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.25px;
  }
  </style>